/**
 * @fileoverview GroupRenderer component for the Storyteller web application.
 * Renders a group of questions within a container.
 *
 * @example
 * // Basic usage
 * <GroupRenderer schema={schema} uischema={uischema} />
 */

import { Layout, LayoutProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { JsonFormsDispatch, withJsonFormsLayoutProps } from '@jsonforms/react'
import { VanillaRendererProps, withVanillaControlProps } from '@jsonforms/vanilla-renderers'
import { Fragment } from 'react'
import css from './GroupRenderer.module.css'

/**
 * @function Group
 * @description Component for rendering a group of questions.
 * @param {LayoutProps & VanillaRendererProps} props - The props for the Group component.
 * @returns {JSX.Element} The Group component.
 */
function Group(props: LayoutProps & VanillaRendererProps) {
    const layout = props.uischema as Layout
    const { path, renderers, cells, schema, enabled } = props

    const customGap = layout.options?.gap
    const style: Record<string, unknown> = {}

    if (customGap) {
        style.gap = customGap
    }

    return (
        <div className={css.container} style={style}>
            {layout.elements.map((child, index) => {
                return (
                    <Fragment key={`${path}-${index}`}>
                        <JsonFormsDispatch
                            renderers={renderers}
                            cells={cells}
                            uischema={child}
                            schema={schema}
                            path={path}
                            enabled={enabled}
                        />
                    </Fragment>
                )
            })}
        </div>
    )
}

export const GroupTester = rankWith(1000, uiTypeIs('Group'))

export default withVanillaControlProps(withJsonFormsLayoutProps(Group))
